import type { Package } from '@/api/packages';
import type { LoyaltyRedeemLine } from '@kgc/api-contract';
import {
  applyTierDiscount,
  buildHolidayNameMap,
  buildHolidaySet,
  calculateCalendarDays,
  calculateTierPrice,
  isHungarianHoliday,
  type BreakdownDay as SharedBreakdownDay,
} from '@kgc/rental-pricing';
import { create } from 'zustand';
import type { PanelId } from './RentalWizardPage';
import { logRentalEvent } from './lib/telemetry';
import type { PackageSelection } from './steps/package-selection-types';
import type { ClassifiedItem } from './steps/package-types';
import type {
  CartItem,
  CartPricing,
  CartPricingItem,
  CrossSellCartItem,
  CrossSellProduct,
  Equipment,
  Partner,
} from './types';
import { EquipmentCategory, EquipmentCondition, PaymentMethod, PricingTier } from './types';

/**
 * BE-story #2: Ár-snapshot egy tartozék-katalógus kiválasztáshoz.
 * A `calculateCartPricing` ebből olvassa az árat (store self-contained).
 * ADR-108 packageSelection-snapshot-konzisztens.
 */
export interface AccessoryCatalogQtySnapshot {
  kind: 'FREE' | 'RENTABLE' | 'CONSIGNMENT';
  dailyRate: number;
  price: number | null;
}

/** Egy tartozék qty + ár-snapshot bejegyzése (per accessoryId per equipmentId). */
export interface AccessoryCatalogQtyEntry extends AccessoryCatalogQtySnapshot {
  qty: number;
}

interface WizardStore {
  // E-105 / ADR-103: multi-panel accordion state (non-sequential)
  openPanels: Set<PanelId>;

  // ── ADR-103 Am-4 / M1.0: tiszta-új step-state (1-indexelt) ──────────────
  // A step-wizard egy NÉZET-SZEGMENTÁLÓ réteg az aggregate-state FÖLÖTT, NEM
  // lineáris állapotgép alatta (D-4.3): a navigáció SOFT (bármikor bárhová),
  // a HARD aggregate-gate kizárólag a véglegesítésen (isSubmittable). Az
  // `openPanels` KOMPLEMENTER marad (a getPanelValidity belőle olvas), a
  // step-state csak a megjelenítés-szegmentálást + a breadcrumb-pipát vezérli.
  /** Aktuális megjelenített lépés (1..4). */
  activeStep: 1 | 2 | 3 | 4;

  // Selected data
  partner: Partner | null;
  costBearerId: string | null;
  /**
   * STORY-1305 AC#5-#7: optional pickup-authorization grant id.
   * PERMANENT (partner-to-partner) or ONE_TIME (rental-bound ad-hoc).
   * null = no grant attached (default — bérlő veszi át a gépet).
   *
   * For ONE_TIME grants captured in-wizard (D1=C lifecycle): the dialog
   * stores granteeData in `pendingOneTimeGrant` and sets `grantId` to the
   * sentinel `PENDING_ONE_TIME_SENTINEL`. The real grant id replaces this
   * value AFTER the rental row is created (handleComplete in RentalWizardPage).
   */
  grantId: string | null;
  /**
   * STORY-1305 AC#6 (D1=C resolution): captured granteeData for an in-wizard
   * ONE_TIME grant. Persisted from the OneTimeGrantDialog form. Materialized
   * into a real `AuthorizationGrant` row by RentalWizardPage.handleComplete
   * AFTER POST /rentals returns the new rental id (atomicity tradeoff: same
   * non-blocking pattern as consignment + accessory cascade).
   */
  pendingOneTimeGrant: PendingOneTimeGrant | null;
  equipment: Equipment | null;
  cart: CartItem[];
  selectedAccessories: Map<string, string[]>;
  startDate: Date | null;
  endDate: Date | null;
  startTime: string | null;
  endTime: string | null;
  useHourlyBilling: boolean;
  pricing: CartPricing | null;
  paymentMethod: PaymentMethod | null;
  depositPaid: boolean;
  notes: string;
  // FE-E loyalty-redeem: draftId = reserve↔commit idempotency-kulcs (reset-kor
  // rotál); loyaltyRedeem = a reserve-preview eredménye (a rental-create body viszi).
  draftId: string;
  loyaltyRedeem: LoyaltyRedeemLine | null;

  // Story 70-1E-a: override expired ID card (E-70). Sent to POST /rentals DTO.
  overrideExpiredId: boolean;
  overrideReason: string;

  // Cross-sell product cart (Story Q-XSL-1)
  crossSellItems: CrossSellCartItem[];

  // Package mode state (P0-1: Wizard ↔ Package Integration)
  selectedPackage: Package | null;
  packageClassifiedItems: ClassifiedItem[];
  consignmentQuantities: Map<string, number>;

  // K3: Accessory suggestion package state (legacy singular shape)
  accessoryPackage: Package | null;
  accessoryQuantities: Record<string, number>; // packageItemId → quantity

  // BE-story #2: tartozék-katalógus qty-store (equipmentId → accessoryId → snapshot)
  // Ár-snapshot a kiválasztáskor (ADR-108 packageSelection-snapshot-konzisztens).
  // A calculateCartPricing ebből olvassa az árat — nem hozzáfér a hook-katalógushoz.
  accessoryCatalogQtys: Map<string, Record<string, AccessoryCatalogQtyEntry>>;
  setAccessoryCatalogQty: (
    equipmentId: string,
    accessoryId: string,
    snapshot: AccessoryCatalogQtySnapshot,
    qty: number
  ) => void; // qty<=0 → törli az entry-t
  getAccessoryCatalogQtys: (equipmentId: string) => Record<string, AccessoryCatalogQtyEntry>;
  clearAccessoryCatalogQtys: (equipmentId: string) => void;

  // E-105 Phase 2 (ADR-103 Addendum 2 / ADR-108 Phase A): normalized 1:N
  // package-selection list. Coexists with `accessoryPackage` during the
  // BERLES_MULTI_PACKAGE rollout — the legacy field is the singular source
  // when the flag is OFF, this array is authoritative when it is ON.
  packageSelections: PackageSelection[];

  // E-105 Sprint 20 W1 Phase 3e (D10): V2 layout filter state (lifted from
  // EquipmentPackageStepV2 local useState). Decorative on cart-only display
  // for now — when V2 search slice (Phase 3f) lands, filters narrow the
  // browse/search equipment results. Empty Set = NO filter (show all).
  // Persists across panel toggles + V1↔V2 flag flips so operator's filter
  // selection doesn't reset on each interaction.
  v2FilterCategories: Set<EquipmentCategory>;
  v2FilterConditions: Set<EquipmentCondition>;

  // E-105 accordion panel actions (non-sequential, multi-panel)
  togglePanel: (id: PanelId) => void;
  openPanel: (id: PanelId) => void;
  closePanel: (id: PanelId) => void;
  setOnlyPanel: (id: PanelId) => void;
  /** Phase 0.2: close all panels — mobile scroll relief. */
  collapseAllPanels: () => void;
  /** Phase 0.2: close all valid panels, open only the invalid ones. */
  expandInvalidOnly: () => void;

  // ── Step-wizard actions (M1.0) — SOFT navigáció, non-sequential ──────────
  /** Jump bármely lépésre (breadcrumb / direct). SOFT — soha nem gate-el. */
  gotoStep: (step: 1 | 2 | 3 | 4) => void;
  /** Előre 1 lépés (clamp 4-nél). SOFT. */
  goNext: () => void;
  /** Vissza 1 lépés (clamp 1-nél). SOFT. */
  goPrev: () => void;
  /**
   * A STEP_PANELS[step] minden paneljének getPanelValidity-je valid? Ez a
   * breadcrumb-pipa EGYETLEN igazság-forrása (live-derived a getPanelValidity-
   * ből, mindig friss — nincs külön `stepsDone` snapshot, ami elavulhatna ha a
   * user step-váltás NÉLKÜL tölt mezőt).
   */
  stepDone: (step: 1 | 2 | 3 | 4) => boolean;

  // Actions
  setPartner: (partner: Partner | null) => void;
  setCostBearer: (id: string | null) => void;
  /** STORY-1305 AC#5-#7: set or clear the pickup-authorization grant. */
  setGrantId: (id: string | null) => void;
  /** STORY-1305 AC#6 (D1=C): set or clear the in-wizard captured ONE_TIME granteeData. */
  setPendingOneTimeGrant: (data: PendingOneTimeGrant | null) => void;
  setEquipment: (equipment: Equipment | null) => void;
  addToCart: (equipment: Equipment, quantity?: number) => void;
  removeFromCart: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  /**
   * FE-A4 — „Ugyanaz mint múltkor" klón-hydrate: a partner legutóbbi bérlésének
   * (resolvelt) kosarát tölti be atomi módon (cart + packageSelections + period
   * felülíródik). A gépek a CTA-komponensben már fel vannak oldva ID→Equipment.
   */
  hydrateFromClone: (input: {
    equipments: Array<{ equipment: Equipment; quantity: number }>;
    packages: Array<{
      equipmentId: string;
      packageId: string;
      packageName: string;
      packageCode: string;
      qtyOverrides?: Record<string, number>;
    }>;
    period: { startDate: Date; endDate: Date };
  }) => void;
  setDates: (startDate: Date, endDate: Date) => void;
  setStartTime: (time: string) => void;
  setEndTime: (time: string) => void;
  setUseHourlyBilling: (use: boolean) => void;
  setNotes: (notes: string) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setDepositPaid: (paid: boolean) => void;
  /** Story 70-1E-a: override expired ID card (maps to DTO overrideExpiredId). */
  setExpiredIdOverride: (override: boolean, reason: string) => void;
  toggleAccessory: (equipmentId: string, accessoryId: string) => void;
  getAccessoriesForEquipment: (equipmentId: string) => string[];
  reset: () => void;
  /** FE-E: a loyalty reserve-preview eredményének tárolása (null = nincs/törölve). */
  setLoyaltyRedeem: (line: LoyaltyRedeemLine | null) => void;

  // Package actions (P0-1)
  setSelectedPackage: (
    pkg: Package | null,
    classifiedItems: ClassifiedItem[],
    consignmentQtys: Map<string, number>
  ) => void;
  updateConsignmentQuantity: (itemId: string, quantity: number) => void;
  clearPackage: () => void;

  // K3: Accessory package actions (legacy singular)
  setAccessoryPackage: (pkg: Package | null) => void;
  updateAccessoryQuantity: (itemId: string, qty: number) => void;
  clearAccessoryPackage: () => void;

  // E-105 Phase 2: normalized packageSelections[] actions (1:N).
  /** Append a new selection. Returns the generated selectionId. */
  addPackageSelection: (input: {
    equipmentId: string;
    packageId: string;
    packageName: string;
    packageCode: string;
    qtyOverrides?: Record<string, number>;
  }) => string;
  /** Remove a selection by selectionId. `reason` distinguishes user vs. cascade for telemetry. */
  removePackageSelection: (selectionId: string, reason?: 'user-action' | 'cart-cascade') => void;
  /** Override a single PackageItem qty inside a selection. */
  setSelectionItemQty: (selectionId: string, itemId: string, qty: number) => void;
  /** Get all selections for an equipmentId — used by per-equipment cards. */
  getSelectionsForEquipment: (equipmentId: string) => PackageSelection[];
  /** True if (equipmentId, packageId) pair is already in selections — drives soft-guard toast. */
  hasDuplicateSelection: (equipmentId: string, packageId: string) => boolean;
  /**
   * Lazy migration: convert legacy `accessoryPackage` (singular) into a
   * `packageSelections` entry attached to the first cart equipment. Idempotent
   * — call any time the BERLES_MULTI_PACKAGE flag flips ON for a tenant whose
   * DRAFT rental was started in singular mode. No-op when there is nothing to
   * migrate or selections already cover the legacy package.
   */
  migrateAccessoryPackageToSelections: () => void;

  // E-105 Sprint 20 W1 Phase 3e: V2 filter actions (multi-select toggle pattern).
  /** Toggle category in V2 filter Set. Re-creates Set to maintain Zustand reference semantics. */
  toggleV2FilterCategory: (cat: EquipmentCategory) => void;
  /** Toggle condition in V2 filter Set. Re-creates Set to maintain Zustand reference semantics. */
  toggleV2FilterCondition: (cond: EquipmentCondition) => void;
  /** Reset both V2 filter Sets to empty. */
  clearV2Filters: () => void;

  // Cross-sell actions (Story Q-XSL-1)
  addCrossSellItem: (product: CrossSellProduct, quantity?: number) => void;
  removeCrossSellItem: (itemId: string) => void;
  updateCrossSellQuantity: (itemId: string, quantity: number) => void;
  clearCrossSellItems: () => void;
  getCrossSellTotal: () => number;

  // Computed
  /** E-105 Story 105-3: per-panel validity + summary for accordion headers. */
  getPanelValidity: (id: PanelId) => ValidationState;
  /** E-105 Story 105-3: aggregate-szintű submit-képesség. */
  isSubmittable: () => boolean;
  isInCart: (equipmentId: string) => boolean;
  getCartItem: (equipmentId: string) => CartItem | undefined;
  getCartTotal: () => number;
}

/** E-105 Story 105-3: per-panel validation eredmény. */
export interface ValidationState {
  isValid: boolean;
  summary: string;
  missingFields?: string[];
}

/**
 * STORY-1305 AC#6 (D1=C): granteeData captured in-wizard for a ONE_TIME grant
 * that will be materialized after the rental row is created.
 */
export interface PendingOneTimeGrant {
  name: string;
  idCardNumber?: string;
  phone?: string;
  address?: string;
}

/**
 * Sentinel `grantId` value while a ONE_TIME grant is captured in-wizard but
 * not yet materialized server-side. Replaced with the real grant id after
 * POST /rentals + POST /auth-grants/one-time both succeed.
 */
export const PENDING_ONE_TIME_SENTINEL = 'pending-one-time';

/**
 * ADR-103 Am-4 / M1.0: step → panel-id mapping (A-mapping, kliens-ratify
 * 2026-06-01). Single Source of Truth a `stepDone(n)` aggregációhoz ÉS a
 * step-szegmens render-sorrendhez. A panel-komponensek VÁLTOZATLANOK, csak
 * más step alá csoportosulnak — a sorrend NEM gate-szemantika (I-1..I-4
 * sértetlen). `costBearer` step1-be (a store partner-ből prefilleli), a
 * `packages` step2 alatt optional-valid marad (az EquipmentPackageStepV2
 * olvasztja, nem dup-renderel).
 */
export const STEP_PANELS: Record<1 | 2 | 3 | 4, PanelId[]> = {
  1: ['partner', 'costBearer'],
  2: ['equipment', 'packages'],
  3: ['schedule'],
  4: ['payment'],
};

// ── Hungarian holiday helpers (ADR-108 P2: use @kgc/rental-pricing) ──
// Previously this file held 150 lines of duplicated calendar/holiday math
// mirroring the backend. Replaced with a shared pure-function import.

type BreakdownDayCalc = SharedBreakdownDay;

function buildBreakdownDays(
  startDate: Date,
  endDate: Date
): {
  breakdownDays: BreakdownDayCalc[];
  billableDays: number;
  sundayCount: number;
  holidayCount: number;
  weeklyDiscountDays: number;
  isMonthlyMode: boolean;
} {
  const start = new Date(
    Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  );
  const end = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()));
  const diffMs = end.getTime() - start.getTime();
  const calendarDays = Math.round(diffMs / 86_400_000) + 1;
  const isMonthlyMode = calendarDays >= 28;

  if (isMonthlyMode) {
    let weekdays = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const dow = cur.getUTCDay();
      if (dow >= 1 && dow <= 5) weekdays++;
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return {
      breakdownDays: [],
      billableDays: weekdays,
      sundayCount: 0,
      holidayCount: 0,
      weeklyDiscountDays: 0,
      isMonthlyMode: true,
    };
  }

  // Shared holiday data — single source of truth with backend rental-pricing.
  const holidaySet = buildHolidaySet(start, end);
  const holidayNames = buildHolidayNameMap(start, end);

  const breakdownDays: BreakdownDayCalc[] = [];
  let billableDays = 0;
  let sundayCount = 0;
  let holidayCount = 0;
  const cur = new Date(start);

  while (cur <= end) {
    const dow = cur.getUTCDay();
    const y = cur.getUTCFullYear();
    const m = cur.getUTCMonth() + 1;
    const d = cur.getUTCDate();
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const hKey = `${cur.getUTCFullYear()}-${cur.getUTCMonth()}-${cur.getUTCDate()}`;

    let type: 'WORKDAY' | 'SUNDAY' | 'HOLIDAY';
    let value: number;
    let holidayName: string | undefined;

    if (dow === 0) {
      type = 'SUNDAY';
      value = 0.5;
      sundayCount++;
    } else if (isHungarianHoliday(cur, holidaySet)) {
      type = 'HOLIDAY';
      value = 0.5;
      holidayCount++;
      holidayName = holidayNames.get(hKey);
    } else {
      type = 'WORKDAY';
      value = 1;
    }

    billableDays += value;
    breakdownDays.push({ date: dateStr, dayOfWeek: dow, type, value, holidayName });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }

  const weeklyDiscountDays = calendarDays >= 7 ? Math.floor(calendarDays / 7) : 0;
  if (weeklyDiscountDays > 0) {
    billableDays = Math.max(0.5, billableDays - weeklyDiscountDays);
  }

  return {
    breakdownDays,
    billableDays,
    sundayCount,
    holidayCount,
    weeklyDiscountDays,
    isMonthlyMode,
  };
}

/** Calculate hours between two HH:MM time strings. Returns at least 1 hour. */
function calculateHoursBetween(startTime: string, endTime: string): number {
  const startParts = startTime.split(':');
  const endParts = endTime.split(':');
  const startH = Number(startParts[0]) || 0;
  const startM = Number(startParts[1]) || 0;
  const endH = Number(endParts[0]) || 0;
  const endM = Number(endParts[1]) || 0;
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  const diffMinutes = endMinutes - startMinutes;
  // Round up to full hours, minimum 1 hour
  return Math.max(1, Math.ceil(diffMinutes / 60));
}

interface HourlyBillingParams {
  useHourlyBilling: boolean;
  startTime: string | null;
  endTime: string | null;
}

function calculateCartPricing(
  cart: CartItem[],
  startDate: Date,
  endDate: Date,
  hourlyParams?: HourlyBillingParams,
  accessoryCatalogQtys?: Map<string, Record<string, AccessoryCatalogQtyEntry>>
): CartPricing {
  // QA-88 (P0 MONEY): a varázsló és a BE UGYANAZT a naptári-nap definíciót használja.
  // A korábbi `ceil(ms / 24h) + 1` két külön okból tért el a szervertől:
  //   (a) órákból számolt, nem naptári napokból ⇒ 49 óra 4 napot adott 3 helyett;
  //   (b) a BE UTC-napon számolt, a varázsló helyi napon ⇒ a „holnap, időpont nélkül"
  //       alakú bérlésre 2-t mutatott és 1-et hozott létre (12 700 Ft vs. 6 350 Ft).
  // A megosztott `calculateCalendarDays` mindkettőt megszünteti: egy definíció, magyar naptár.
  const durationDays = calculateCalendarDays(startDate, endDate);
  const breakdown = buildBreakdownDays(startDate, endDate);

  const isHourly =
    hourlyParams?.useHourlyBilling === true &&
    hourlyParams.startTime != null &&
    hourlyParams.endTime != null &&
    cart.some(item => item.equipment.hourlyRate != null && item.equipment.hourlyRate > 0);

  const totalHours = isHourly
    ? calculateHoursBetween(hourlyParams!.startTime!, hourlyParams!.endTime!)
    : undefined;

  const items: CartPricingItem[] = cart.map(item => {
    const eq = item.equipment;
    let tier: PricingTier;
    let baseAmount: number;
    let itemHourlyRate: number | undefined;
    let itemDurationHours: number | undefined;

    // Use hourly billing if enabled and this equipment has an hourly rate
    if (isHourly && eq.hourlyRate != null && eq.hourlyRate > 0 && totalHours != null) {
      tier = PricingTier.HOURLY;
      baseAmount = totalHours * eq.hourlyRate;
      itemHourlyRate = eq.hourlyRate;
      itemDurationHours = totalHours;
      // ⚠️ Q-T053 free-week/month guard (a #592 BE-fix FE-vetülete): a WEEKLY/MONTHLY tier CSAK akkor
      // választható, ha a gépnek VAN weekly/monthly rátája. Daily-only gépnél (weeklyRate/monthlyRate
      // null→0) e guard nélkül a `weeks * 0` INGYENESSÉ tenné a hetet/hónapot (≥7 napos bérlés → súlyos
      // undercharge). 0-ráta → essen a DAILY ágba (durationDays × dailyRate). Az időtartam-% kedvezmény
      // (applyTierDiscount) külön marad.
    } else {
      // ADR-037 Am.3 `D-2`: EGY tier-aritmetika. A varázsló eddig saját, egyszerűsített
      // if-láncot futtatott — a `Math.min`-cap (heti-cap · M-016 havi inverzió-védelem ·
      // fallback-cap) KIZÁRÓLAG a checkout oldalon élt, ezért a varázsló éjféles, egész napos
      // bemeneten is TÖBBET mutatott, mint amennyi a bérlésből létrejött (27 nap: +5 000 Ft).
      // A `HOURLY` ág fölötte marad — az a FE-enum sajátja, a megosztott fn nem ismeri.
      const tierResult = calculateTierPrice(
        durationDays,
        eq.dailyRate,
        eq.weeklyRate,
        eq.monthlyRate
      );
      tier = PricingTier[tierResult.tier];
      baseAmount = tierResult.grossAmount;
    }

    const itemSubtotal = baseAmount * item.quantity;
    const deposit = eq.depositAmount * item.quantity;

    return {
      cartItemId: item.id,
      equipmentId: eq.id,
      equipmentName: eq.name,
      tier,
      dailyRate: eq.dailyRate,
      hourlyRate: itemHourlyRate,
      durationDays,
      durationHours: itemDurationHours,
      itemSubtotal,
      // itemVat/itemTotal omitted: NAV-compliant VAT is calculated at aggregate level
      itemVat: 0,
      itemTotal: itemSubtotal,
      deposit,
    };
  });

  // NAV-compliant aggregate rounding: calculate VAT on net amount (after discount), not per-item
  const subtotal = items.reduce((sum, i) => sum + i.itemSubtotal, 0);
  const depositTotal = items.reduce((sum, i) => sum + i.deposit, 0);

  // BE-story #2: tartozék-katalógus árazás aggregálása (proto pricing.js 20-55 alapján)
  // rentAccNet: RENTABLE belongs to → machineNet + rentAccNet = lineNet (VAT-alap, discount ELŐTT)
  //   proto: total = Math.round(a.dailyRate * qty * days); rentAccNet += total
  // consignNet: CONSIGNMENT → külön track, NEM bérleti díj, NEM kedvezményezett
  //   proto: total = (a.price || 0) * qty; consignNet += total
  // FREE: 0 mindkét fajta totalhoz
  // PROTO-KONSZISZTENCIA: a proto a rentAccNet-et a lineNet-be teszi (machineRentNet + rentAccNet),
  //   és CSAK a machineRentNet-re fut discount (per proto: discount = ci.discountPct ami a gép sora).
  //   ITT: a tartozék-bér NEM kap volume-discountot (csak a gép-bérleti sor) — kövesd a proto-t.
  let accessoryRentNet = 0;
  let consignmentNet = 0;
  let accessoryCount = 0;

  if (accessoryCatalogQtys && accessoryCatalogQtys.size > 0) {
    for (const [, accRecord] of accessoryCatalogQtys) {
      for (const [, entry] of Object.entries(accRecord)) {
        if (entry.qty <= 0) continue;
        accessoryCount += entry.qty;
        if (entry.kind === 'RENTABLE') {
          accessoryRentNet += Math.round(entry.dailyRate * entry.qty * durationDays);
        } else if (entry.kind === 'CONSIGNMENT') {
          consignmentNet += (entry.price ?? 0) * entry.qty;
        }
        // FREE: 0 hozzáadás (pro proto)
      }
    }
  }

  // Story 78-1 → P0-1 (FE-2 slice-2): ADR-048 kalendárnap-kedvezmény a kanonikus tier-SSoT-ból
  // (@kgc/rental-pricing applyTierDiscount, #502 value-pin 10/15/25/30) — a korábbi inline sávok
  // kiváltva, hogy a FE-preview és a BE /calculate EGYETLEN forrásból éljen (measured-azonos:
  // Math.round((subtotal × pct) / 100)). A discount csak a gép-bérleti sorra (subtotal); a
  // rentAccNet NEM kedvezményezett (proto: discount = ci.discountPct × machineRentNet).
  const discountTotal = applyTierDiscount(subtotal, durationDays).discountAmount;

  // rentAccNet a net-be olvad (VAT-alap, gép-bérleti discounttal CSÖKKENTETT gép-sor + rentAccNet)
  const netAfterDiscount = subtotal - discountTotal + accessoryRentNet;
  const vatAmount = Math.round(netAfterDiscount * 0.27);
  const grandTotal = netAfterDiscount + vatAmount;

  return {
    items,
    durationDays,
    totalHours,
    useHourlyBilling: isHourly,
    subtotal,
    discountTotal,
    vatAmount,
    grandTotal,
    depositTotal,
    breakdownDays: isHourly ? [] : breakdown.breakdownDays,
    weeklyDiscountDays: isHourly ? 0 : breakdown.weeklyDiscountDays,
    isMonthlyMode: isHourly ? false : breakdown.isMonthlyMode,
    billableDays: isHourly ? 0 : breakdown.billableDays,
    accessoryRentNet,
    consignmentNet,
    accessoryCount,
  };
}

/** Recalculate pricing from current cart and dates. Returns null if cart empty or dates missing. */
function recalcPricing(
  cart: CartItem[],
  startDate: Date | null,
  endDate: Date | null,
  hourlyParams?: HourlyBillingParams,
  accessoryCatalogQtys?: Map<string, Record<string, AccessoryCatalogQtyEntry>>
): CartPricing | null {
  if (cart.length === 0 || !startDate || !endDate) return null;
  return calculateCartPricing(cart, startDate, endDate, hourlyParams, accessoryCatalogQtys);
}

let cartIdCounter = 0;
let crossSellIdCounter = 0;

// E-105: első panel default nyitott
const INITIAL_OPEN_PANELS: ReadonlyArray<PanelId> = ['partner'];

const initialState = {
  openPanels: new Set<PanelId>(INITIAL_OPEN_PANELS),
  activeStep: 1 as 1 | 2 | 3 | 4,
  partner: null,
  costBearerId: null as string | null,
  grantId: null as string | null,
  pendingOneTimeGrant: null as PendingOneTimeGrant | null,
  equipment: null,
  cart: [] as CartItem[],
  selectedAccessories: new Map<string, string[]>(),
  startDate: null,
  endDate: null,
  startTime: null as string | null,
  endTime: null as string | null,
  useHourlyBilling: false,
  pricing: null as CartPricing | null,
  paymentMethod: null as PaymentMethod | null,
  depositPaid: false,
  notes: '',
  draftId: crypto.randomUUID(),
  loyaltyRedeem: null as LoyaltyRedeemLine | null,
  overrideExpiredId: false,
  overrideReason: '',
  crossSellItems: [] as CrossSellCartItem[],
  selectedPackage: null as Package | null,
  packageClassifiedItems: [] as ClassifiedItem[],
  consignmentQuantities: new Map<string, number>(),
  accessoryPackage: null as Package | null,
  accessoryQuantities: {} as Record<string, number>,
  packageSelections: [] as PackageSelection[],
  v2FilterCategories: new Set<EquipmentCategory>(),
  v2FilterConditions: new Set<EquipmentCondition>(),
  // BE-story #2: tartozék-katalógus qty-store (üres Map induláskor)
  accessoryCatalogQtys: new Map<string, Record<string, AccessoryCatalogQtyEntry>>(),
};

export const useWizardStore = create<WizardStore>((set, get) => ({
  ...initialState,

  // ─── E-105 Accordion panel actions ───────────────────────────────────────

  togglePanel: id => {
    const { openPanels } = get();
    const next = new Set(openPanels);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    set({ openPanels: next });
  },

  openPanel: id => {
    const { openPanels } = get();
    if (openPanels.has(id)) return;
    set({ openPanels: new Set([...openPanels, id]) });
  },

  closePanel: id => {
    const { openPanels } = get();
    if (!openPanels.has(id)) return;
    const next = new Set(openPanels);
    next.delete(id);
    set({ openPanels: next });
  },

  setOnlyPanel: id => {
    set({ openPanels: new Set<PanelId>([id]) });
  },

  // Phase 0.2 — 2026-04-14: collapse-all + expand-invalid-only.
  // UX driver (Sally): 5 panel nyitva ≈ 2000px mobil scroll. Ezek a gombok
  // a WizardShell headerben élnek, a pultos egy tappal fókuszálhat.
  collapseAllPanels: () => {
    set({ openPanels: new Set<PanelId>() });
  },

  expandInvalidOnly: () => {
    const getValidity = get().getPanelValidity;
    // Phase 2: 'packages' is optional → never invalid → never auto-expanded.
    const panelOrder: PanelId[] = [
      'partner',
      'equipment',
      'packages',
      'costBearer',
      'schedule',
      'payment',
    ];
    const invalid = panelOrder.filter(id => !getValidity(id).isValid);
    set({ openPanels: new Set<PanelId>(invalid) });
  },

  // ── Step-wizard navigáció (M1.0) — SOFT, non-sequential ──────────────────
  // Csak az `activeStep`-et állítja. A breadcrumb-pipa a live `stepDone(n)`-ből
  // derived (mindig friss) — nincs külön snapshot-state. A gate KIZÁRÓLAG a
  // véglegesítésen (isSubmittable) — itt nincs blokk (SOFT).
  gotoStep: step => set({ activeStep: step }),

  goNext: () => {
    const next = Math.min(4, get().activeStep + 1) as 1 | 2 | 3 | 4;
    get().gotoStep(next);
  },

  goPrev: () => {
    const prev = Math.max(1, get().activeStep - 1) as 1 | 2 | 3 | 4;
    get().gotoStep(prev);
  },

  stepDone: step => {
    const getValidity = get().getPanelValidity;
    return STEP_PANELS[step].every(id => getValidity(id).isValid);
  },

  setPartner: partner => {
    const { paymentMethod } = get();
    // Clear invalid payment method when partner changes (transfer only for companies)
    const clearedMethod =
      paymentMethod === PaymentMethod.TRANSFER && partner?.type !== 'COMPANY'
        ? null
        : paymentMethod;
    // Pre-fill cost bearer from partner if available
    const costBearerId = partner?.costBearerId ?? null;
    // Story 70-1E-a: any partner change resets the expired-id override — the
    // new partner's expiry may differ, and an override cannot "carry" across.
    // STORY-1305 P3 (review 2026-05-13): any partner change also clears the
    // grant binding — the previously selected grant belongs to the prior
    // grantor and cannot transfer across (cross-grantor leak prevention).
    set({
      partner,
      paymentMethod: clearedMethod,
      costBearerId,
      overrideExpiredId: false,
      overrideReason: '',
      grantId: null,
      pendingOneTimeGrant: null,
    });
  },

  setExpiredIdOverride: (override, reason) =>
    set({ overrideExpiredId: override, overrideReason: reason }),

  setCostBearer: id => set({ costBearerId: id }),

  setGrantId: id => {
    // Clearing the grant also clears any in-wizard captured ONE_TIME data —
    // the user is opting out of grants entirely. Setting a non-null id (either
    // PENDING_ONE_TIME_SENTINEL or a real PERMANENT/materialized id) leaves
    // pendingOneTimeGrant untouched so the dialog's captured data survives
    // until handleComplete replaces the sentinel with the real grant row id.
    if (id === null) set({ grantId: null, pendingOneTimeGrant: null });
    else set({ grantId: id });
  },

  setPendingOneTimeGrant: data => set({ pendingOneTimeGrant: data }),

  setEquipment: equipment => {
    // Bridge: single equipment selection also manages cart for EquipmentStep compatibility
    const { startDate, endDate, startTime, endTime, useHourlyBilling, accessoryCatalogQtys } =
      get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    let newCart: CartItem[];
    if (equipment) {
      cartIdCounter++;
      newCart = [
        {
          id: `cart-${cartIdCounter}`,
          equipment,
          quantity: 1,
          addedAt: new Date(),
        },
      ];
    } else {
      newCart = [];
    }
    set({
      equipment,
      cart: newCart,
      pricing: recalcPricing(newCart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  addToCart: (equipment, quantity = 1) => {
    const { cart, startDate, endDate, startTime, endTime, useHourlyBilling, accessoryCatalogQtys } =
      get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    const existing = cart.find(item => item.equipment.id === equipment.id);
    let newCart: CartItem[];

    if (existing) {
      newCart = cart.map(item =>
        item.equipment.id === equipment.id ? { ...item, quantity: item.quantity + quantity } : item
      );
    } else {
      cartIdCounter++;
      newCart = [
        ...cart,
        {
          id: `cart-${cartIdCounter}`,
          equipment,
          quantity,
          addedAt: new Date(),
        },
      ];
    }

    set({
      cart: newCart,
      pricing: recalcPricing(newCart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  removeFromCart: cartItemId => {
    const {
      cart,
      startDate,
      endDate,
      startTime,
      endTime,
      useHourlyBilling,
      packageSelections,
      accessoryCatalogQtys,
    } = get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    const removedItem = cart.find(item => item.id === cartItemId);
    const newCart = cart.filter(item => item.id !== cartItemId);

    // Phase 2 cascade: drop packageSelections attached to the removed equipment.
    // Telemetry fires per dropped selection so the post-ship review can
    // distinguish user-driven vs. cascade removals.
    let nextSelections = packageSelections;
    if (removedItem) {
      const orphaned = packageSelections.filter(
        sel => sel.equipmentId === removedItem.equipment.id
      );
      if (orphaned.length > 0) {
        for (const sel of orphaned) {
          logRentalEvent('rental.package_selection.removed', {
            selectionId: sel.selectionId,
            equipmentId: sel.equipmentId,
            packageId: sel.packageId,
            removedReason: 'cart-cascade',
          });
        }
        nextSelections = packageSelections.filter(
          sel => sel.equipmentId !== removedItem.equipment.id
        );
      }
    }

    // Phase 0.3 (Amelia-gotcha 2026-04-14): cascade cleanup of singular accessoryPackage
    // when cart empties. Prevents orphan state where K3 consignment flow fires with a
    // package attached to an equipment that is no longer in the rental.
    const cascadeCleanup =
      newCart.length === 0
        ? { accessoryPackage: null, accessoryQuantities: {}, packageSelections: [] }
        : { packageSelections: nextSelections };
    set({
      cart: newCart,
      pricing: recalcPricing(newCart, startDate, endDate, hp, accessoryCatalogQtys),
      ...cascadeCleanup,
    });
  },

  updateQuantity: (cartItemId, quantity) => {
    const {
      cart,
      startDate,
      endDate,
      startTime,
      endTime,
      useHourlyBilling,
      packageSelections,
      accessoryCatalogQtys,
    } = get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    let newCart: CartItem[];
    const removedItem = quantity <= 0 ? cart.find(item => item.id === cartItemId) : undefined;

    if (quantity <= 0) {
      newCart = cart.filter(item => item.id !== cartItemId);
    } else {
      newCart = cart.map(item => (item.id === cartItemId ? { ...item, quantity } : item));
    }

    // Phase 2 cascade: same logic as removeFromCart for the qty→0 path.
    let nextSelections = packageSelections;
    if (removedItem) {
      const orphaned = packageSelections.filter(
        sel => sel.equipmentId === removedItem.equipment.id
      );
      if (orphaned.length > 0) {
        for (const sel of orphaned) {
          logRentalEvent('rental.package_selection.removed', {
            selectionId: sel.selectionId,
            equipmentId: sel.equipmentId,
            packageId: sel.packageId,
            removedReason: 'cart-cascade',
          });
        }
        nextSelections = packageSelections.filter(
          sel => sel.equipmentId !== removedItem.equipment.id
        );
      }
    }

    // Phase 0.3 cascade cleanup mirrors removeFromCart — quantity=0 path may empty cart.
    const cascadeCleanup =
      newCart.length === 0
        ? { accessoryPackage: null, accessoryQuantities: {}, packageSelections: [] }
        : { packageSelections: nextSelections };
    set({
      cart: newCart,
      pricing: recalcPricing(newCart, startDate, endDate, hp, accessoryCatalogQtys),
      ...cascadeCleanup,
    });
  },

  clearCart: () =>
    set({
      cart: [],
      pricing: null,
      // Phase 0.3: explicit clear — clearCart is a "start fresh" intent.
      accessoryPackage: null,
      accessoryQuantities: {},
      // Phase 2: drop all packageSelections too. Cascade telemetry skipped here
      // because clearCart is an explicit user "reset" intent, not an orphan event.
      packageSelections: [],
    }),

  hydrateFromClone: input => {
    const { startTime, endTime, useHourlyBilling, accessoryCatalogQtys } = get();
    const { startDate, endDate } = input.period;
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };

    const newCart: CartItem[] = input.equipments.map(({ equipment, quantity }) => {
      cartIdCounter++;
      return { id: `cart-${cartIdCounter}`, equipment, quantity, addedAt: new Date() };
    });

    const newSelections: PackageSelection[] = input.packages.map(pkg => ({
      selectionId: crypto.randomUUID(),
      equipmentId: pkg.equipmentId,
      packageId: pkg.packageId,
      packageName: pkg.packageName,
      packageCode: pkg.packageCode,
      ...(pkg.qtyOverrides ? { qtyOverrides: pkg.qtyOverrides } : {}),
    }));

    set({
      cart: newCart,
      packageSelections: newSelections,
      startDate,
      endDate,
      pricing: recalcPricing(newCart, startDate, endDate, hp, accessoryCatalogQtys),
    });
    logRentalEvent('rental.clone.hydrated', {
      equipmentCount: newCart.length,
      packageCount: newSelections.length,
    });
  },

  setDates: (startDate, endDate) => {
    const { cart, startTime, endTime, useHourlyBilling, accessoryCatalogQtys } = get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    set({
      startDate,
      endDate,
      pricing: recalcPricing(cart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  setStartTime: time => {
    const { cart, startDate, endDate, endTime, useHourlyBilling, accessoryCatalogQtys } = get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime: time, endTime };
    set({
      startTime: time,
      pricing: recalcPricing(cart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  setEndTime: time => {
    const { cart, startDate, endDate, startTime, useHourlyBilling, accessoryCatalogQtys } = get();
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime: time };
    set({
      endTime: time,
      pricing: recalcPricing(cart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  setUseHourlyBilling: use => {
    const { cart, startDate, endDate, startTime, endTime, accessoryCatalogQtys } = get();
    const hp: HourlyBillingParams = { useHourlyBilling: use, startTime, endTime };
    set({
      useHourlyBilling: use,
      pricing: recalcPricing(cart, startDate, endDate, hp, accessoryCatalogQtys),
    });
  },

  setNotes: notes => set({ notes }),

  setPaymentMethod: method => {
    // Store-level validation: transfer only for company partners
    const { partner } = get();
    if (method === PaymentMethod.TRANSFER && partner?.type !== 'COMPANY') {
      return;
    }
    set({ paymentMethod: method });
  },

  setDepositPaid: (paid: boolean) => {
    set({ depositPaid: paid });
  },

  toggleAccessory: (equipmentId, accessoryId) => {
    const { selectedAccessories } = get();
    const newMap = new Map(selectedAccessories);
    const current = newMap.get(equipmentId) ?? [];
    const idx = current.indexOf(accessoryId);
    const isAdd = idx < 0;
    if (idx >= 0) {
      newMap.set(
        equipmentId,
        current.filter(id => id !== accessoryId)
      );
    } else {
      newMap.set(equipmentId, [...current, accessoryId]);
    }
    set({ selectedAccessories: newMap });
    // Phase 0.4 Telemetry: only log on add (not remove). Tracks accessories
    // picked OUTSIDE of any package — high count here signals package-model
    // weakness (John: "30%+ manual bypass → bigger question").
    if (isAdd) {
      logRentalEvent('rental.accessory.manual_added', { equipmentId, accessoryId });
    }
  },

  getAccessoriesForEquipment: equipmentId => {
    return get().selectedAccessories.get(equipmentId) ?? [];
  },

  // ─── Package actions (P0-1: Wizard ↔ Package Integration) ───────────────────

  setSelectedPackage: (pkg, classifiedItems, consignmentQtys) => {
    set({
      selectedPackage: pkg,
      packageClassifiedItems: classifiedItems,
      consignmentQuantities: new Map(consignmentQtys),
    });
  },

  updateConsignmentQuantity: (itemId, quantity) => {
    const { consignmentQuantities } = get();
    const next = new Map(consignmentQuantities);
    next.set(itemId, Math.max(0, quantity));
    set({ consignmentQuantities: next });
  },

  clearPackage: () => {
    set({
      selectedPackage: null,
      packageClassifiedItems: [],
      consignmentQuantities: new Map<string, number>(),
    });
  },

  // ─── K3: Accessory package actions ──────────────────────────────────────────

  setAccessoryPackage: pkg => {
    if (!pkg) {
      set({ accessoryPackage: null, accessoryQuantities: {} });
      return;
    }
    // Phase 0.4 Telemetry: distinguish fresh attach vs. swap (proxy for "I wanted
    // two packages but only one slot available"). Drives ADR-103 Addendum G3 gate.
    const { accessoryPackage: previousPackage, cart } = get();
    const firstEquipmentId = cart[0]?.equipment.id;
    if (previousPackage && previousPackage.id !== pkg.id) {
      logRentalEvent('rental.package.swap_attempt', {
        equipmentId: firstEquipmentId,
        packageId: pkg.id,
        previousPackageId: previousPackage.id,
      });
    } else if (!previousPackage) {
      logRentalEvent('rental.package.attached', {
        equipmentId: firstEquipmentId,
        packageId: pkg.id,
      });
    }
    const quantities: Record<string, number> = {};
    for (const item of pkg.items) {
      quantities[item.id] = item.quantity;
    }
    set({ accessoryPackage: pkg, accessoryQuantities: quantities });
  },

  updateAccessoryQuantity: (itemId, qty) => {
    const { accessoryQuantities } = get();
    set({ accessoryQuantities: { ...accessoryQuantities, [itemId]: Math.max(0, qty) } });
  },

  clearAccessoryPackage: () => {
    set({ accessoryPackage: null, accessoryQuantities: {} });
  },

  // ─── E-105 Phase 2: packageSelections[] (1:N) ──────────────────────────────

  addPackageSelection: input => {
    const selectionId = crypto.randomUUID();
    const selection: PackageSelection = {
      selectionId,
      equipmentId: input.equipmentId,
      packageId: input.packageId,
      packageName: input.packageName,
      packageCode: input.packageCode,
      ...(input.qtyOverrides ? { qtyOverrides: input.qtyOverrides } : {}),
    };
    set({ packageSelections: [...get().packageSelections, selection] });
    logRentalEvent('rental.package_selection.created', {
      selectionId,
      equipmentId: input.equipmentId,
      packageId: input.packageId,
    });
    return selectionId;
  },

  removePackageSelection: (selectionId, reason = 'user-action') => {
    const { packageSelections } = get();
    const target = packageSelections.find(s => s.selectionId === selectionId);
    if (!target) return;
    set({ packageSelections: packageSelections.filter(s => s.selectionId !== selectionId) });
    logRentalEvent('rental.package_selection.removed', {
      selectionId,
      equipmentId: target.equipmentId,
      packageId: target.packageId,
      removedReason: reason,
    });
  },

  setSelectionItemQty: (selectionId, itemId, qty) => {
    const { packageSelections } = get();
    const next = packageSelections.map(sel => {
      if (sel.selectionId !== selectionId) return sel;
      const overrides = { ...(sel.qtyOverrides ?? {}), [itemId]: Math.max(0, qty) };
      return { ...sel, qtyOverrides: overrides };
    });
    set({ packageSelections: next });
  },

  getSelectionsForEquipment: equipmentId => {
    return get().packageSelections.filter(sel => sel.equipmentId === equipmentId);
  },

  hasDuplicateSelection: (equipmentId, packageId) => {
    return get().packageSelections.some(
      sel => sel.equipmentId === equipmentId && sel.packageId === packageId
    );
  },

  // ─── E-105 Sprint 20 W1 Phase 3e: V2 filter actions ──────────────────────

  toggleV2FilterCategory: cat => {
    const next = new Set(get().v2FilterCategories);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    set({ v2FilterCategories: next });
  },

  toggleV2FilterCondition: cond => {
    const next = new Set(get().v2FilterConditions);
    if (next.has(cond)) next.delete(cond);
    else next.add(cond);
    set({ v2FilterConditions: next });
  },

  clearV2Filters: () => {
    set({
      v2FilterCategories: new Set<EquipmentCategory>(),
      v2FilterConditions: new Set<EquipmentCondition>(),
    });
  },

  // ─── BE-story #2: tartozék-katalógus qty-store actions ──────────────────────

  setAccessoryCatalogQty: (equipmentId, accessoryId, snapshot, qty) => {
    const { accessoryCatalogQtys, cart, startDate, endDate, startTime, endTime, useHourlyBilling } =
      get();
    const next = new Map(accessoryCatalogQtys);
    const eqRecord = { ...(next.get(equipmentId) ?? {}) };

    if (qty <= 0) {
      // qty<=0 → töröljük az entry-t
      delete eqRecord[accessoryId];
    } else {
      eqRecord[accessoryId] = { ...snapshot, qty };
    }

    if (Object.keys(eqRecord).length === 0) {
      next.delete(equipmentId);
    } else {
      next.set(equipmentId, eqRecord);
    }

    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    set({ accessoryCatalogQtys: next, pricing: recalcPricing(cart, startDate, endDate, hp, next) });
  },

  getAccessoryCatalogQtys: equipmentId => {
    return get().accessoryCatalogQtys.get(equipmentId) ?? {};
  },

  clearAccessoryCatalogQtys: equipmentId => {
    const { accessoryCatalogQtys, cart, startDate, endDate, startTime, endTime, useHourlyBilling } =
      get();
    const next = new Map(accessoryCatalogQtys);
    next.delete(equipmentId);
    const hp: HourlyBillingParams = { useHourlyBilling, startTime, endTime };
    set({ accessoryCatalogQtys: next, pricing: recalcPricing(cart, startDate, endDate, hp, next) });
  },

  migrateAccessoryPackageToSelections: () => {
    const { accessoryPackage, accessoryQuantities, cart, packageSelections } = get();
    if (!accessoryPackage) return;
    const firstEq = cart[0]?.equipment;
    if (!firstEq) return;
    // Idempotency: if a selection for this (equipmentId, packageId) already
    // exists, skip — the migration was already performed in a prior session.
    const alreadyMigrated = packageSelections.some(
      sel => sel.equipmentId === firstEq.id && sel.packageId === accessoryPackage.id
    );
    if (alreadyMigrated) return;
    const selectionId = crypto.randomUUID();
    const overrides = { ...accessoryQuantities };
    set({
      packageSelections: [
        ...packageSelections,
        {
          selectionId,
          equipmentId: firstEq.id,
          packageId: accessoryPackage.id,
          packageName: accessoryPackage.name,
          packageCode: accessoryPackage.packageCode,
          qtyOverrides: overrides,
        },
      ],
      // Clear legacy fields after migration — single source of truth from now on.
      accessoryPackage: null,
      accessoryQuantities: {},
    });
    logRentalEvent('rental.package_selection.created', {
      selectionId,
      equipmentId: firstEq.id,
      packageId: accessoryPackage.id,
    });
  },

  // ─── Cross-sell product actions (Story Q-XSL-1) ─────────────────────────────

  addCrossSellItem: (product, quantity = 1) => {
    const { crossSellItems } = get();
    const existing = crossSellItems.find(item => item.product.id === product.id);

    if (existing) {
      const updated = crossSellItems.map(item =>
        item.product.id === product.id
          ? {
              ...item,
              quantity: item.quantity + quantity,
              lineTotal: (item.quantity + quantity) * item.unitPrice,
            }
          : item
      );
      set({ crossSellItems: updated });
    } else {
      crossSellIdCounter++;
      const newItem: CrossSellCartItem = {
        id: `xsell-${crossSellIdCounter}`,
        product,
        quantity,
        unitPrice: product.price,
        lineTotal: product.price * quantity,
      };
      set({ crossSellItems: [...crossSellItems, newItem] });
    }
  },

  removeCrossSellItem: itemId => {
    const { crossSellItems } = get();
    set({ crossSellItems: crossSellItems.filter(item => item.id !== itemId) });
  },

  updateCrossSellQuantity: (itemId, quantity) => {
    const { crossSellItems } = get();
    if (quantity <= 0) {
      set({ crossSellItems: crossSellItems.filter(item => item.id !== itemId) });
    } else {
      set({
        crossSellItems: crossSellItems.map(item =>
          item.id === itemId ? { ...item, quantity, lineTotal: item.unitPrice * quantity } : item
        ),
      });
    }
  },

  clearCrossSellItems: () => set({ crossSellItems: [] }),

  getCrossSellTotal: () => {
    return get().crossSellItems.reduce((sum, item) => sum + item.lineTotal, 0);
  },

  reset: () => {
    cartIdCounter = 0;
    crossSellIdCounter = 0;
    set({
      ...initialState,
      openPanels: new Set<PanelId>(INITIAL_OPEN_PANELS),
      selectedAccessories: new Map<string, string[]>(),
      consignmentQuantities: new Map<string, number>(),
      accessoryPackage: null,
      accessoryQuantities: {},
      // BE-story #2: tartozék-katalógus qty-store törlése reset-kor
      accessoryCatalogQtys: new Map<string, Record<string, AccessoryCatalogQtyEntry>>(),
      // új session → új idempotency-kulcs + tiszta loyalty-preview
      draftId: crypto.randomUUID(),
      loyaltyRedeem: null,
    });
  },

  setLoyaltyRedeem: line => set({ loyaltyRedeem: line }),

  // ─── Story 105-3: per-panel validity + aggregate submit-képesség ──────────

  getPanelValidity: id => {
    const { partner, costBearerId, cart, startDate, endDate, paymentMethod, depositPaid, pricing } =
      get();

    switch (id) {
      case 'partner':
        if (partner === null) return { isValid: false, summary: '', missingFields: ['partner'] };
        return { isValid: true, summary: partner.name };

      case 'equipment': {
        if (cart.length === 0) return { isValid: false, summary: '', missingFields: ['equipment'] };
        const total = pricing?.grandTotal ?? 0;
        return {
          isValid: true,
          summary: `${cart.length} tétel · ${total.toLocaleString('hu-HU')} Ft`,
        };
      }

      case 'costBearer': {
        // STORY-1305 AC#4: self-pay (costBearerId === partner.id) NOT override; partner default NOT override
        const isSelfPay = costBearerId === partner?.id;
        const isPartnerDefault = costBearerId === (partner?.costBearerId ?? null);
        const isOverride = costBearerId !== null && !isSelfPay && !isPartnerDefault;
        return { isValid: true, summary: isOverride ? `Override: ${costBearerId}` : '' };
      }

      case 'schedule': {
        if (startDate === null || endDate === null) {
          return { isValid: false, summary: '', missingFields: ['schedule'] };
        }
        const days = pricing?.durationDays ?? 0;
        return {
          isValid: true,
          summary: `${startDate.toLocaleDateString('hu-HU')} – ${endDate.toLocaleDateString('hu-HU')} (${days} nap)`,
        };
      }

      case 'packages': {
        // Phase 2: optional panel — always valid. Summary lists count + first
        // package name when at least one selection exists.
        const sels = get().packageSelections;
        if (sels.length === 0) return { isValid: true, summary: '' };
        const firstName = sels[0]?.packageName ?? '';
        const more = sels.length > 1 ? ` +${sels.length - 1}` : '';
        return { isValid: true, summary: `${sels.length} csomag · ${firstName}${more}` };
      }

      case 'payment': {
        if (paymentMethod === null) {
          return { isValid: false, summary: '', missingFields: ['payment'] };
        }
        return {
          isValid: true,
          summary: `${paymentMethod}${depositPaid ? ' · kaució fizetve' : ''}`,
        };
      }
    }
  },

  isSubmittable: () => {
    const validity = get().getPanelValidity;
    return (
      validity('partner').isValid &&
      validity('equipment').isValid &&
      validity('schedule').isValid &&
      validity('payment').isValid
    );
  },

  isInCart: equipmentId => {
    return get().cart.some(item => item.equipment.id === equipmentId);
  },

  getCartItem: equipmentId => {
    return get().cart.find(item => item.equipment.id === equipmentId);
  },

  getCartTotal: () => {
    return get().cart.reduce((total, item) => total + item.equipment.dailyRate * item.quantity, 0);
  },
}));
